import * as React from 'react';
import styled, { css } from 'styled-components';

import { hideNumberInputSpinner } from './NumberInput';
import { Input } from './Input';
import { themeGet, is } from '../../theme';

export const Placeholder = styled.span`
  position: absolute;
  bottom: 10px;
  transition: 0.2s ease all;
  pointer-events: none;
  left: ${themeGet.space(2)};
  color: ${themeGet.colors('swirl')};
  font-weight: ${themeGet.fontWeight('lighter')};
  font-size: ${themeGet.fontSize(6)};
  background-color: ${themeGet.colors('white')};
  padding: 0 2px;
`;

const inputErrorStyles = css`
  border-color: ${themeGet.colors('error')};
  ~ ${Placeholder} {
    color: ${themeGet.colors('error')};
  }
` as any;

const smallPlaceholder = css`
  ~ ${Placeholder} {
    font-size: ${themeGet.fontSize(2)};
    left: 14px;
    bottom: 34px;
  }
`;

const InputStyled = styled(Input)<{ hasError: boolean; isDirty: boolean }>`
  ${hideNumberInputSpinner};
  &:focus,
  &:not(:placeholder-shown) {
    ${smallPlaceholder}
  }

  &:focus,
  &:not(:-ms-input-placeholder) {
    ${smallPlaceholder}
  }

  &:not(:focus):invalid {
    ${is('isDirty')`
      ${inputErrorStyles}
    `}
  }

  ${is('hasError')`
    ${inputErrorStyles}
  `}
`;

export const InputAnimatedStyled = styled.div`
  position: relative;
`;

export const InputAnimated: React.FC<
  React.InputHTMLAttributes<HTMLInputElement> & { hasError?: boolean; validateImmediately?: boolean }
> = ({ placeholder, children, hasError = false, validateImmediately = false, ...rest }) => {
  // required inputs are immediately marked as invalid - even if the user did
  // not touch this input yet
  const [isDirty, setIsDirty] = React.useState(validateImmediately);
  const handleBlur = (args) => {
    setIsDirty(true);
    rest?.onBlur?.(args);
  };

  return (
    <InputAnimatedStyled>
      {/* the empty placeholder is required here, so we can use css selector ':placeholder-shown' */}
      <InputStyled {...rest} onBlur={handleBlur} hasError={hasError} placeholder=" " isDirty={isDirty} />
      <Placeholder>{placeholder}</Placeholder>
      {children}
    </InputAnimatedStyled>
  );
};
